import Vue from 'vue';

// constants.
import { HR_GROUPS } from '@/constants/hr';
import { ASSETS_GROUPS } from '@/constants/assets';
import { MANUALS_GROUPS } from '@/constants/manuals';

// utils.
import {
  isAdmin,
  hasRole,
  hasAnyRole,
  hasPermission,
  isCurrentUser,
} from '@/utils/permissions';

export default Vue.extend({
  computed: {
    /**
     * Returns `true` if the current user has manual admin permissions.
     *
     * @returns {boolean}
     */
    isManualsAdmin(): boolean {
      return this.hasPermission([MANUALS_GROUPS.ADMIN]);
    },
    /**
     * Returns `true` if the current user has asset admin permissions.
     *
     * @returns {boolean}
     */
    isAssetAdmin(): boolean {
      return this.hasPermission([ASSETS_GROUPS.ADMIN]);
    },
    /**
     * Returns `true` if the current user has HR admin permissions.
     *
     * @returns {boolean}
     */
    isHRAdmin(): boolean {
      return this.hasPermission([HR_GROUPS.ADMIN]);
    },
    /**
     * Returns `true` if the current user has HR finance permissions.
     *
     * @returns {boolean}
     */
    isHRFinance(): boolean {
      return this.hasPermission([HR_GROUPS.FINANCE]);
    },
    /**
     * Returns `true` if the current user has HR viewing permissions.
     *
     * @returns {boolean}
     */
    isHRViewer(): boolean {
      return this.hasPermission([HR_GROUPS.VIEWER]);
    },
    isHRContractViewer(): boolean {
      return this.hasPermission([HR_GROUPS.CONTRACT]);
    },
  },
  methods: {
    /**
     * Checks if the current user is an admin.
     *
     * @returns {boolean}
     */
    isAdmin() {
      return isAdmin();
    },

    /**
     * Checks if the current user id matched a given userId.
     *
     * @param userId The userId to compare with current logged in userId.
     * @returns {boolean}
     */
    isCurrentUser(userId: string) {
      return isCurrentUser(userId);
    },

    /**
     * Checks if the current user has the specified role.
     *
     * @param {string} role
     * @returns {boolean}
     */
    hasRole(role: string) {
      return hasRole(role);
    },

    /**
     * Checks if the current user has any role from the list.
     *
     * @param {string[]} roles
     * @returns {boolean}
     */
    hasAnyRole(roles: string[]) {
      return hasAnyRole(roles);
    },

    /**
     * Checks if the current user has permission based on required roles or admin status.
     *
     * @param {string[]} requiredRoles
     * @returns {boolean}
     */
    hasPermission(requiredRoles: string[]) {
      return hasPermission(requiredRoles);
    },
  },
});
